/**
 * @file CatalogIndexPage.tsx
 * @description Custom catalog index page that adds a subtitle to the header.
 *
 * Wraps the standard Backstage CatalogIndexPage content with a PageWithHeader
 * that includes a subtitle, matching the pattern used by the Teams page.
 *
 * This is rendered as a child outlet of the plugin's CatalogIndexPage route,
 * which checks useOutlet() and renders the outlet instead of DefaultCatalogPage
 * when one is provided.
 */

import {
  PageWithHeader,
  Content,
  ContentHeader,
  SupportButton,
} from '@backstage/core-components';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { CatalogTable } from '@backstage/plugin-catalog';
import {
  EntityListProvider,
  type EntityListContextProps,
  CatalogFilterLayout,
  DefaultFilters,
  EntityRefLink,
  EntityRefLinks,
} from '@backstage/plugin-catalog-react';
import type { TableColumn } from '@backstage/core-components';
import type { CatalogTableRow } from '@backstage/plugin-catalog';

/**
 * Keep columns stable per entity kind, omitting irrelevant relationships.
 * Lifecycle remains excluded. Sorting and search come from the stock columns;
 * name, owner and system renders are replaced only to hide the kind icons.
 */
const customColumns = ({
  filters,
}: EntityListContextProps): TableColumn<CatalogTableRow>[] => {
  // The kind filter keeps the URL's spelling, so compare case-insensitively.
  const kind = filters.kind?.value?.toLocaleLowerCase('en-US');
  const name = CatalogTable.columns.createNameColumn({ defaultKind: kind });
  return [
    // Hidden column so the table search also matches metadata.title.
    CatalogTable.columns.createTitleColumn({ hidden: true }),
    {
      ...name,
      width: '30%',
      cellStyle: { minWidth: 220 },
      render: ({ entity }) => (
        <EntityRefLink
          entityRef={entity}
          defaultKind={kind || 'Component'}
          hideIcon
        />
      ),
    },
    ...(['component', 'api', 'resource'].includes(kind ?? '')
      ? [
          {
            ...CatalogTable.columns.createSystemColumn(),
            render: ({ resolved }: CatalogTableRow) => (
              <EntityRefLinks
                entityRefs={resolved.partOfSystemRelations}
                defaultKind="system"
                hideIcons
              />
            ),
          },
        ]
      : []),
    ...(!['user', 'group', 'location'].includes(kind ?? '')
      ? [
          {
            ...CatalogTable.columns.createOwnerColumn(),
            render: ({ resolved }: CatalogTableRow) => (
              <EntityRefLinks
                entityRefs={resolved.ownedByRelations}
                defaultKind="group"
                hideIcons
              />
            ),
          },
        ]
      : []),
    ...(!['system', 'domain', 'user'].includes(kind ?? '')
      ? [CatalogTable.columns.createSpecTypeColumn({ hidden: false })]
      : []),
    CatalogTable.columns.createMetadataDescriptionColumn(),
    CatalogTable.columns.createTagsColumn(),
  ];
};

export const CustomCatalogIndexPage = () => {
  const configApi = useApi(configApiRef);
  const orgName =
    configApi.getOptionalString('organization.name') ?? 'Univerus';

  return (
    <PageWithHeader
      themeId="catalog"
      title={`${orgName} Catalog`}
      subtitle={`Services, systems, and resources across ${orgName}`}
      pageTitleOverride="Catalog"
    >
      <Content>
        <ContentHeader title="">
          <SupportButton>All your software catalog entities</SupportButton>
        </ContentHeader>
        <EntityListProvider>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              <DefaultFilters
                initialKind="system"
                initiallySelectedFilter="all"
              />
            </CatalogFilterLayout.Filters>
            <CatalogFilterLayout.Content>
              <CatalogTable columns={customColumns} />
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </EntityListProvider>
      </Content>
    </PageWithHeader>
  );
};
